import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface BookingCheckInLogicProps {
  booking: any;
  devices: any[];
  currentAccessCode: any;
  timeSlotCodes?: any[]; // Make this optional for backward compatibility
  createAccessCode: any;
  hasEnabledDevices: boolean;
  canCheckIn: boolean;
  provider?: any;
}

export const useBookingCheckInLogic = ({
  booking,
  devices,
  currentAccessCode,
  timeSlotCodes = [], // Provide default empty array
  createAccessCode,
  hasEnabledDevices,
  canCheckIn,
  provider
}: BookingCheckInLogicProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleCheckIn = async () => {
    console.log('=== CHECK IN BUTTON CLICKED ===');
    console.log('Booking data:', booking);
    console.log('Booking ID:', booking?.id);
    console.log('TIME SLOT ID from booking object:', booking?.time_slot_id);
    console.log('Current access code:', currentAccessCode);
    console.log('Time slot codes:', timeSlotCodes);
    console.log('Available devices:', devices);
    console.log('Has enabled devices:', hasEnabledDevices);
    console.log('Can check in:', canCheckIn);
    
    if (!booking) {
      console.error('CHECK IN ERROR: No booking data available');
      toast({
        title: "Error",
        description: "Booking data not available. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }
    
    if (!canCheckIn) {
      console.error('CHECK IN ERROR: Check-in not allowed at this time');
      toast({
        title: "Check-in Not Available",
        description: "Check-in is not available at this time.",
        variant: "destructive",
      });
      return;
    }
    
    // Check if booking end time is in the past
    const bookingEndDateTime = new Date(`${booking.booking_date}T${booking.end_time}Z`);
    const currentTime = new Date();
    
    if (bookingEndDateTime <= currentTime) {
      console.error('CHECK IN ERROR: Booking end time is in the past');
      const endTimeFormatted = bookingEndDateTime.toLocaleString();
      toast({
        title: "Check-in Not Available",
        description: `This booking ended at ${endTimeFormatted}. Cannot generate access code for past bookings.`,
        variant: "destructive",
      });
      return;
    }
    
    console.log('CHECK IN: Booking ID:', booking.id);
    console.log('CHECK IN: Time Slot ID from booking:', booking.time_slot_id);
    
    // Find a device for this provider/room
    const availableDevice = devices?.find(device => device.seam_device_id && device.is_enabled);
    
    console.log('CHECK IN: Available device search result:', availableDevice);
    
    if (!availableDevice?.seam_device_id) {
      console.error('CHECK IN ERROR: No available device with seam_device_id found');
      
      toast({
        title: "Access Control Not Available",
        description: "This location doesn't have access control devices configured yet. Please contact the provider for manual entry instructions.",
        variant: "destructive",
      });
      return;
    }
    
    // CRITICAL FIX: If there's already an access code, just update the booking check-in status and RETURN
    if (currentAccessCode) {
      console.log('CHECK IN: Access code already exists, updating booking check-in status only:', currentAccessCode);
      console.log('CHECK IN: Current access code time_slot_id:', currentAccessCode.time_slot_id);
      console.log('CHECK IN: Booking time_slot_id:', booking.time_slot_id);
      
      try {
        // Directly update the booking with check-in timestamp
        const { error: updateError } = await supabase
          .from('bookings')
          .update({
            checked_in_at: new Date().toISOString()
          })
          .eq('id', booking.id);

        if (updateError) {
          console.error('CHECK IN ERROR: Failed to update booking:', updateError);
          toast({
            title: "Check-in Failed",
            description: "Failed to complete check-in. Please try again.",
            variant: "destructive",
          });
          return;
        }

        console.log('CHECK IN SUCCESS: Booking updated with check-in timestamp');
        
        // FIXED: Use the properly injected queryClient
        console.log('CHECK IN: Invalidating queries using proper queryClient');
        
        // Invalidate all possible query keys that might contain this booking data
        await queryClient.invalidateQueries({ queryKey: ['access-codes'] });
        await queryClient.invalidateQueries({ queryKey: ['booking'] });
        await queryClient.invalidateQueries({ queryKey: ['bookings'] });
        await queryClient.invalidateQueries({ queryKey: ['provider-booking-detail'] });
        await queryClient.invalidateQueries({ queryKey: ['provider-bookings'] });
        
        // Force an immediate refetch of the specific booking
        await queryClient.refetchQueries({ 
          queryKey: ['booking', booking.id],
          exact: true 
        });
        
        // Also try to invalidate any queries that might be using the booking ID
        await queryClient.invalidateQueries({ 
          predicate: (query) => {
            return query.queryKey.includes(booking.id) || 
                   query.queryKey.includes('booking') ||
                   query.queryKey.includes('access-codes');
          }
        });
        
        console.log('CHECK IN: All queries invalidated and refetched');
        
        // On mobile, scroll to access details section to show the code
        setTimeout(() => {
          if (window.innerWidth <= 768) {
            const accessDetailsElement = document.getElementById('access-details-section');
            if (accessDetailsElement) {
              const elementRect = accessDetailsElement.getBoundingClientRect();
              const headerHeight = 80;
              const targetPosition = window.pageYOffset + elementRect.top - headerHeight;
              
              window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
              });
            }
          }
        }, 1000);
        
        // CRITICAL: Return here to prevent creating a new access code
        return;
        
      } catch (error) {
        console.error('CHECK IN ERROR: Failed to update booking:', error);
        toast({
          title: "Check-in Failed",
          description: "Failed to complete check-in. Please try again.",
          variant: "destructive",
        });
        return;
      }
    }
    
    // NEW LOGIC: Check if there's a reusable access code for this time slot
    if (timeSlotCodes && timeSlotCodes.length > 0) {
      const reusableCode = timeSlotCodes[0]; // Get the first (most recent) time slot code
      
      console.log('CHECK IN: Found reusable access code for time slot:', reusableCode);
      console.log('CHECK IN: Reusing code:', reusableCode.code, 'for time_slot_id:', booking.time_slot_id);
      
      try {
        // Create a new access_codes record for this booking that references the same code
        const { data: newAccessCodeRecord, error: insertError } = await supabase
          .from('access_codes')
          .insert({
            booking_id: booking.id,
            code: reusableCode.code,
            start_time: reusableCode.start_time,
            time_slot_id: booking.time_slot_id,
            seam_access_code_id: reusableCode.seam_access_code_id,
            is_active: true,
          })
          .select()
          .single();

        if (insertError) {
          console.error('CHECK IN ERROR: Failed to create access code record for reusable code:', insertError);
          throw insertError;
        }

        // Update the booking with check-in timestamp
        const { error: updateError } = await supabase
          .from('bookings')
          .update({
            checked_in_at: new Date().toISOString()
          })
          .eq('id', booking.id);

        if (updateError) {
          console.error('CHECK IN ERROR: Failed to update booking check-in time:', updateError);
        } else {
          console.log('CHECK IN SUCCESS: Updated booking with check-in timestamp');
        }

        console.log('CHECK IN SUCCESS: Reused existing access code:', {
          reused_code: reusableCode.code,
          reused_seam_access_code_id: reusableCode.seam_access_code_id,
          new_record_for_booking: newAccessCodeRecord,
          time_slot_id: booking.time_slot_id,
          note: 'Successfully reused existing access code and created record for this booking'
        });
        
        // Invalidate and refetch queries
        await queryClient.invalidateQueries({ queryKey: ['access-codes'] });
        await queryClient.invalidateQueries({ queryKey: ['booking'] });
        await queryClient.invalidateQueries({ queryKey: ['bookings'] });
        await queryClient.invalidateQueries({ queryKey: ['provider-booking-detail'] });
        await queryClient.invalidateQueries({ queryKey: ['provider-bookings'] });
        
        await queryClient.refetchQueries({ 
          queryKey: ['booking', booking.id],
          exact: true 
        });
        
        // On mobile, scroll to access details section
        setTimeout(() => {
          if (window.innerWidth <= 768) {
            const accessDetailsElement = document.getElementById('access-details-section');
            if (accessDetailsElement) {
              const elementRect = accessDetailsElement.getBoundingClientRect();
              const headerHeight = 80;
              const targetPosition = window.pageYOffset + elementRect.top - headerHeight;
              
              window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
              });
            }
          }
        }, 300);
        
        // CRITICAL: Return here since we've successfully reused a code
        return;
        
      } catch (error) {
        console.error('CHECK IN ERROR: Failed to reuse access code, will try creating new one:', error);
        // Don't return here - fall through to create a new code
      }
    }
    
    // ONLY CREATE NEW CODE IF NO EXISTING CODE FOUND AND NO REUSABLE CODE
    console.log('CHECK IN: No existing or reusable access code found, proceeding to create new one');
    console.log('CHECK IN: Creating NEW access code for time_slot_id:', booking.time_slot_id);
    
    // TIMEZONE FIX: Always use UTC times for backend operations
    let accessStartTime: string;
    let endTime: string;
    
    try {
      // Use UTC times if available (should be ISO strings from useBooking), otherwise construct from database times
      if (booking.utc_start_time && booking.utc_end_time) {
        // If these are already ISO strings, use them directly
        if (booking.utc_start_time.includes('T') && booking.utc_start_time.includes('Z')) {
          accessStartTime = booking.utc_start_time;
          endTime = booking.utc_end_time;
          
          console.log('CHECKIN LOGIC: Using UTC ISO strings from booking:', {
            booking_id: booking.id,
            time_slot_id: booking.time_slot_id,
            utc_start_iso: accessStartTime,
            utc_end_iso: endTime,
            source: 'Direct UTC ISO strings from booking object'
          });
        } else {
          // If these are just HH:MM:SS times, construct ISO strings
          accessStartTime = `${booking.booking_date}T${booking.utc_start_time}Z`;
          endTime = `${booking.booking_date}T${booking.utc_end_time}Z`;
          
          console.log('CHECKIN LOGIC: Constructed UTC ISO from booking UTC times:', {
            booking_id: booking.id,
            time_slot_id: booking.time_slot_id,
            booking_date: booking.booking_date,
            utc_start_time: booking.utc_start_time,
            utc_end_time: booking.utc_end_time,
            utc_start_iso: accessStartTime,
            utc_end_iso: endTime,
            source: 'Constructed from UTC times in booking object'
          });
        }
      } else {
        // Fallback: use display times and assume they were originally UTC
        accessStartTime = `${booking.booking_date}T${booking.start_time}Z`;
        endTime = `${booking.booking_date}T${booking.end_time}Z`;
        
        console.log('CHECKIN LOGIC: Fallback - using display times as UTC:', {
          booking_id: booking.id,
          time_slot_id: booking.time_slot_id,
          booking_date: booking.booking_date,
          display_start_time: booking.start_time,
          display_end_time: booking.end_time,
          utc_start_iso: accessStartTime,
          utc_end_iso: endTime,
          source: 'Fallback - display times treated as UTC',
          warning: 'This may cause timezone issues if display times were converted'
        });
      }
      
      // Validate the constructed times
      const startDate = new Date(accessStartTime);
      const endDate = new Date(endTime);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Invalid date format constructed');
      }
      
      console.log('CHECKIN LOGIC: Final time validation passed:', {
        access_start_time: accessStartTime,
        end_time: endTime,
        time_slot_id: booking.time_slot_id,
        parsed_start_utc: startDate.toISOString(),
        parsed_end_utc: endDate.toISOString(),
        parsed_start_local: startDate.toLocaleString('en-GB', { timeZone: 'Europe/London' }),
        parsed_end_local: endDate.toLocaleString('en-GB', { timeZone: 'Europe/London' }),
        validation_status: 'PASSED'
      });
    } catch (timeError) {
      console.error('CHECKIN LOGIC: Time construction error:', timeError);
      toast({
        title: "Check-in Failed",
        description: `Invalid booking time format: ${timeError.message}`,
        variant: "destructive",
      });
      return;
    }
    
    // Apply early access ONLY if explicitly enabled
    if (provider?.early_access_enabled === true && provider?.early_access_minutes > 0) {
      const earlyAccessMs = provider.early_access_minutes * 60 * 1000;
      const originalStartTimeDate = new Date(accessStartTime);
      const originalTimestamp = originalStartTimeDate.getTime();
      const adjustedTimestamp = originalTimestamp - earlyAccessMs;
      const adjustedStartTimeDate = new Date(adjustedTimestamp);
      
      // CRITICAL: Ensure we're subtracting, not adding
      accessStartTime = adjustedStartTimeDate.toISOString();
      
      console.log('EARLY ACCESS: Applied early access adjustment:', {
        booking_id: booking.id,
        time_slot_id: booking.time_slot_id,
        original_access_start_time: `${booking.booking_date}T${booking.start_time}Z`,
        early_access_minutes: provider.early_access_minutes,
        early_access_ms: earlyAccessMs,
        original_timestamp: originalTimestamp,
        adjusted_timestamp: adjustedTimestamp,
        time_difference_ms: originalTimestamp - adjustedTimestamp,
        time_difference_minutes: (originalTimestamp - adjustedTimestamp) / (60 * 1000),
        operation: 'SUBTRACT early access minutes from start time',
        original_start_iso: originalStartTimeDate.toISOString(),
        adjusted_start_iso: accessStartTime,
        verification: {
          should_be_minutes_earlier: provider.early_access_minutes,
          actually_is_minutes_earlier: (originalTimestamp - adjustedTimestamp) / (60 * 1000)
        }
      });
    } else {
      console.log('EARLY ACCESS SKIPPED:', {
        earlyAccessEnabled: provider?.early_access_enabled,
        earlyAccessMinutes: provider?.early_access_minutes,
        reason: provider?.early_access_enabled !== true ? 'Not enabled' : 'No minutes configured'
      });
    }
    
    console.log('CHECKIN LOGIC: Creating NEW access code - no existing or reusable code found:', {
      booking_id: booking.id,
      time_slot_id: booking.time_slot_id,
      seam_device_id: availableDevice.seam_device_id,
      times_being_sent_to_seam: {
        start_time: accessStartTime,
        end_time: endTime,
        note: 'These are the exact times being sent to Seam API for NEW code creation'
      }
    });
    
    try {
      const result = await createAccessCode.mutateAsync({
        bookingId: booking.id,
        seamDeviceId: availableDevice.seam_device_id,
        startTime: accessStartTime,
        endTime: endTime,
        timeSlotId: booking.time_slot_id,
      });
      
      console.log('CHECK IN SUCCESS: Access code created:', result);
      console.log('CHECK IN SUCCESS: Created access code for time_slot_id:', booking.time_slot_id);
      
      // On mobile, scroll to access details section to show it fully
      if (window.innerWidth <= 768) {
        setTimeout(() => {
          const accessDetailsElement = document.getElementById('access-details-section');
          if (accessDetailsElement) {
            const elementRect = accessDetailsElement.getBoundingClientRect();
            const headerHeight = 80;
            const targetPosition = window.pageYOffset + elementRect.top - headerHeight;
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }, 300);
      }
    } catch (error) {
      console.error('CHECK IN ERROR: Failed to create access code:', error);
      console.error('CHECK IN ERROR: Failed to create access code for time_slot_id:', booking.time_slot_id);
      
      toast({
        title: "Check-in Failed",
        description: `Failed to generate access code: ${error?.message || 'Unknown error'}. Please try again or contact support.`,
        variant: "destructive",
      });
    }
  };

  return { handleCheckIn };
};
